import { useEffect, useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { Link } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { useKeys } from '../../ui/keys.ts';
import { Drawer, ErrorBox, Kbd, Loading } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { IntegrationPanel } from './IntegrationPanel.tsx';
import { NavigationPanel } from './NavigationPanel.tsx';
import { PlacementsPanel } from './PlacementsPanel.tsx';
import { RoutesPanel } from './RoutesPanel.tsx';
import { CLICKOUT_LABELS, REVEAL_LABELS, TYPE_LABELS, type AiMode, type SiteDetail, type SiteOptions } from './types.ts';
import { AI_MODE_LABELS, CAPABILITY_LABELS, ConfirmButton, blankToNull } from './ui.tsx';

const TABS = [
  { id: 'settings', label: 'Settings' },
  { id: 'navigation', label: 'Navigation' },
  { id: 'addresses', label: 'Addresses' },
  { id: 'placements', label: 'Placements' },
  { id: 'integration', label: 'Proxy integration' },
  { id: 'routes', label: 'Routes' },
] as const;
type Tab = (typeof TABS)[number]['id'];

export function SiteDrawer({ siteRef, onClose }: { siteRef: string; onClose: () => void }) {
  const changes = useChanges();
  const site = useOp<SiteDetail>('admin.sites.get', { ref: siteRef }, changes);
  const options = useOp<SiteOptions>('admin.sites.options');
  const [tab, setTab] = useState<Tab>('settings');
  useKeys(Object.fromEntries(TABS.map((t, index) => [String(index + 1), () => setTab(t.id)])));
  const data = site.data;
  return (
    <Drawer title={data ? data.name : 'Site'} onClose={onClose} wide>
      {site.error ? <ErrorBox error={site.error} onRetry={site.reload} /> : null}
      {!data && site.loading ? <Loading /> : null}
      {data && options.data ? (
        <>
          <p className="muted small">
            {data.organization.name} · {data.brand.name} · {TYPE_LABELS[data.type] ?? data.type} · {data.market} · your role {data.role}
            {' · '}
            <Link to={`/themes?level=site&ref=${data.ref}`}>Theme</Link>
            {' · '}
            <Link to={`/api-keys?site=${data.ref}`}>API keys</Link>
          </p>
          {!data.canAdmin ? <p className="dev-note">Read only: owners and admins of this site change its settings.</p> : null}
          <div className="segmented tabs" role="tablist" aria-label="Site sections">
            {TABS.map((t, index) => (
              <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} aria-pressed={tab === t.id} onClick={() => setTab(t.id)}>
                {t.label} <Kbd>{index + 1}</Kbd>
              </button>
            ))}
          </div>
          {tab === 'settings' ? <SettingsForm site={data} options={options.data} /> : null}
          {tab === 'navigation' ? <NavigationPanel site={data} /> : null}
          {tab === 'addresses' ? <Addresses site={data} /> : null}
          {tab === 'placements' ? <PlacementsPanel site={data} /> : null}
          {tab === 'integration' ? <IntegrationPanel site={data} /> : null}
          {tab === 'routes' ? <RoutesPanel site={data} /> : null}
        </>
      ) : null}
    </Drawer>
  );
}

const TIME_ZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    return [];
  }
})();

interface SettingsDraft {
  name: string;
  type: string;
  status: string;
  defaultLocale: string;
  currency: string;
  timeZone: string;
  clickoutMode: string;
  revealPolicy: string;
  indexable: boolean;
  siteName: string;
  descriptor: string;
  tagline: string;
  affiliateDisclosure: string;
  editorialOwner: string;
  searchConsoleProperty: string;
  thinMerchantMinLiveCoupons: number;
  publisherCategories: boolean;
  commercialDisclosure: boolean;
  showDemoNotice: boolean;
  headHtml: string;
  consentRulesetId: string;
  legalOperator: string;
  legalAddress: string;
  legalRepresentedBy: string;
  legalRegister: string;
  legalVatId: string;
  legalEmail: string;
  legalPhone: string;
  legalResponsible: string;
  consentAutoblocker: boolean;
  consentIdType: 'settings' | 'ruleset';
  mascotName: string;
  mascotEnabled: boolean;
  aiDefault: AiMode | '';
  ai: Record<string, AiMode | ''>;
}

function draftFrom(site: SiteDetail): SettingsDraft {
  const s = site.settings;
  return {
    name: site.name,
    type: site.type,
    status: site.status,
    defaultLocale: site.defaultLocale,
    currency: site.currency,
    timeZone: site.timeZone ?? '',
    clickoutMode: site.clickoutMode,
    revealPolicy: site.revealPolicy,
    indexable: site.indexable,
    siteName: s.siteName ?? '',
    descriptor: s.descriptor ?? '',
    tagline: s.tagline ?? '',
    affiliateDisclosure: s.affiliateDisclosure ?? '',
    editorialOwner: s.editorialOwner ?? '',
    searchConsoleProperty: s.searchConsoleProperty ?? '',
    thinMerchantMinLiveCoupons: s.thinMerchantMinLiveCoupons,
    publisherCategories: s.publisherCategories,
    commercialDisclosure: s.commercialDisclosure,
    showDemoNotice: s.showDemoNotice,
    headHtml: s.headHtml ?? '',
    consentRulesetId: s.consent?.rulesetId ?? '',
    legalOperator: s.legalNotice?.operator ?? '',
    legalAddress: (s.legalNotice?.address ?? []).join('\n'),
    legalRepresentedBy: s.legalNotice?.representedBy ?? '',
    legalRegister: s.legalNotice?.register ?? '',
    legalVatId: s.legalNotice?.vatId ?? '',
    legalEmail: s.legalNotice?.email ?? '',
    legalPhone: s.legalNotice?.phone ?? '',
    legalResponsible: s.legalNotice?.contentResponsible ?? '',
    consentAutoblocker: s.consent?.autoblocker ?? true,
    consentIdType: s.consent?.idType ?? 'settings',
    mascotName: s.mascot?.name ?? '',
    mascotEnabled: s.mascot?.enabled ?? false,
    aiDefault: site.ai.default ?? '',
    ai: Object.fromEntries(site.ai.capabilities.map((c) => [c.capability, c.requested ?? ''])),
  };
}

const RANK: Record<AiMode, number> = { off: 0, suggest: 1, auto: 2 };

function SettingsForm({ site, options }: { site: SiteDetail; options: SiteOptions }) {
  const toast = useToast();
  const [draft, setDraft] = useState(() => draftFrom(site));
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(draftFrom(site)), [site]);
  const set = <K extends keyof SettingsDraft>(key: K, value: SettingsDraft[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const disabled = !site.canAdmin;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const capabilities = Object.fromEntries(Object.entries(draft.ai).filter(([, mode]) => mode !== '')) as Record<string, AiMode>;
    const ai = draft.aiDefault === '' && Object.keys(capabilities).length === 0 ? null : { ...(draft.aiDefault ? { default: draft.aiDefault } : {}), capabilities };
    await runWrite(
      toast,
      'admin.sites.update',
      {
        ref: site.ref,
        name: draft.name,
        type: draft.type,
        status: draft.status,
        defaultLocale: draft.defaultLocale,
        currency: draft.currency,
        timeZone: draft.timeZone.trim() || null,
        clickoutMode: draft.clickoutMode,
        revealPolicy: draft.revealPolicy,
        indexable: draft.indexable,
        settings: {
          siteName: blankToNull(draft.siteName),
          descriptor: blankToNull(draft.descriptor),
          tagline: blankToNull(draft.tagline),
          affiliateDisclosure: blankToNull(draft.affiliateDisclosure),
          editorialOwner: blankToNull(draft.editorialOwner),
          searchConsoleProperty: blankToNull(draft.searchConsoleProperty),
          thinMerchantMinLiveCoupons: draft.thinMerchantMinLiveCoupons,
          publisherCategories: draft.publisherCategories,
          commercialDisclosure: draft.commercialDisclosure,
          showDemoNotice: draft.showDemoNotice,
          headHtml: draft.headHtml.trim() ? draft.headHtml : null,
          legalNotice:
            draft.legalOperator.trim() && draft.legalEmail.trim()
              ? {
                  operator: draft.legalOperator.trim(),
                  address: draft.legalAddress.split('\n').map((line) => line.trim()).filter(Boolean),
                  email: draft.legalEmail.trim(),
                  ...(draft.legalRepresentedBy.trim() ? { representedBy: draft.legalRepresentedBy.trim() } : {}),
                  ...(draft.legalRegister.trim() ? { register: draft.legalRegister.trim() } : {}),
                  ...(draft.legalVatId.trim() ? { vatId: draft.legalVatId.trim() } : {}),
                  ...(draft.legalPhone.trim() ? { phone: draft.legalPhone.trim() } : {}),
                  ...(draft.legalResponsible.trim() ? { contentResponsible: draft.legalResponsible.trim() } : {}),
                }
              : null,
          consent: draft.consentRulesetId.trim() ? { provider: 'usercentrics', rulesetId: draft.consentRulesetId.trim(), idType: draft.consentIdType, autoblocker: draft.consentAutoblocker } : null,
          mascot: draft.mascotName.trim() ? { name: draft.mascotName.trim(), enabled: draft.mascotEnabled } : null,
          ai,
        },
      },
      'Site settings saved',
    );
    setSaving(false);
  };

  const ceilingFor = (capability: string) => site.ai.capabilities.find((c) => c.capability === capability)?.ceiling ?? 'off';
  const effectiveFor = (capability: string): AiMode => {
    const requested = (draft.ai[capability] || draft.aiDefault || 'suggest') as AiMode;
    const ceiling = ceilingFor(capability);
    return RANK[requested] <= RANK[ceiling] ? requested : ceiling;
  };
  const above = (capability: string) => {
    const requested = draft.ai[capability] || draft.aiDefault;
    return requested !== '' && RANK[requested as AiMode] > RANK[ceilingFor(capability)];
  };
  const anyAbove = options.aiCapabilities.some(above);

  return (
    <form onSubmit={save} aria-label="Site settings">
      <fieldset disabled={disabled}>
        <h3 className="section-title">Site</h3>
        <div className="form-grid cols-4">
          <label className="span-2">
            Internal name
            <input required minLength={2} value={draft.name} onChange={(e) => set('name', e.target.value)} />
          </label>
          <label>
            Type
            <select value={draft.type} onChange={(e) => set('type', e.target.value)}>
              {options.types.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t] ?? t}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={draft.status} onChange={(e) => set('status', e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
          <label>
            Language
            <input
              value={draft.defaultLocale}
              onChange={(e) => set('defaultLocale', e.target.value)}
              pattern="[a-z]{2}(-[A-Z]{2})?"
              list="site-locales"
              title={`Site texts exist in: ${options.languages.join(', ')}. Use language-COUNTRY, e.g. de-AT.`}
            />
            <datalist id="site-locales">
              {[...new Set([...options.markets.map((m) => m.locale), ...options.languages])].map((locale) => (
                <option key={locale} value={locale} />
              ))}
            </datalist>
          </label>
          <label>
            Currency
            <input value={draft.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} pattern="[A-Z]{3}" maxLength={3} />
          </label>
          <label>
            Time zone
            <input
              value={draft.timeZone}
              onChange={(e) => set('timeZone', e.target.value)}
              placeholder={`${site.marketTimeZone} (market)`}
              list="site-time-zones"
              title="The site's clock: when a day and a month begin, when dated coupons start and end on the pages, when the cache is refreshed. Empty = the market's zone."
            />
            <datalist id="site-time-zones">
              {TIME_ZONES.map((zone) => (
                <option key={zone} value={zone} />
              ))}
            </datalist>
          </label>
          <label>
            Clickout
            <select value={draft.clickoutMode} onChange={(e) => set('clickoutMode', e.target.value)}>
              {options.clickoutModes.map((m) => (
                <option key={m} value={m}>
                  {CLICKOUT_LABELS[m] ?? m}
                </option>
              ))}
            </select>
          </label>
          <label>
            Codes shown
            <select value={draft.revealPolicy} onChange={(e) => set('revealPolicy', e.target.value)}>
              {options.revealPolicies.map((m) => (
                <option key={m} value={m}>
                  {REVEAL_LABELS[m] ?? m}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-check span-2">
            <input type="checkbox" checked={draft.indexable} onChange={(e) => set('indexable', e.target.checked)} /> Search engines may index this site
          </label>
          <label className="inline-check span-2">
            <input type="checkbox" checked={draft.showDemoNotice} onChange={(e) => set('showDemoNotice', e.target.checked)} /> Show the "sample data" banner
          </label>
        </div>

        <h3 className="section-title">Legal notice</h3>
        <p className="muted small">Shown on the site's legal notice page (/legal) and linked in the footer. Operator and email are required; without them the page does not exist.</p>
        <div className="form-grid cols-4">
          <label className="span-2">
            Operator (legal name and form)
            <input value={draft.legalOperator} placeholder="Jetscale UG (haftungsbeschränkt)" onChange={(e) => set('legalOperator', e.target.value)} />
          </label>
          <label className="span-2">
            Managing director
            <input value={draft.legalRepresentedBy} onChange={(e) => set('legalRepresentedBy', e.target.value)} />
          </label>
          <label className="span-2">
            Address (one line per row)
            <textarea rows={3} value={draft.legalAddress} onChange={(e) => set('legalAddress', e.target.value)} />
          </label>
          <label className="span-2">
            Commercial register
            <input value={draft.legalRegister} placeholder="Amtsgericht Charlottenburg, HRB …" onChange={(e) => set('legalRegister', e.target.value)} />
          </label>
          <label>
            VAT ID
            <input value={draft.legalVatId} onChange={(e) => set('legalVatId', e.target.value)} />
          </label>
          <label>
            Email
            <input type="email" value={draft.legalEmail} onChange={(e) => set('legalEmail', e.target.value)} />
          </label>
          <label>
            Phone
            <input value={draft.legalPhone} onChange={(e) => set('legalPhone', e.target.value)} />
          </label>
          <label>
            Responsible for content
            <input value={draft.legalResponsible} onChange={(e) => set('legalResponsible', e.target.value)} />
          </label>
        </div>

        <h3 className="section-title">Cookie consent</h3>
        <div className="form-grid cols-4">
          <label className="span-2">
            Usercentrics ID
            <input value={draft.consentRulesetId} placeholder="e.g. RNazxJXGNdZSsg" spellCheck={false} onChange={(e) => set('consentRulesetId', e.target.value)} />
            <span className="muted small">Usercentrics admin → Implementation → script tag. Empty = no banner.</span>
          </label>
          <label>
            The ID is a
            <select value={draft.consentIdType} onChange={(e) => set('consentIdType', e.target.value as 'settings' | 'ruleset')}>
              <option value="settings">Configuration (data-settings-id)</option>
              <option value="ruleset">Ruleset (data-ruleset-id)</option>
            </select>
          </label>
          <label className="inline-check span-2">
            <input type="checkbox" checked={draft.consentAutoblocker} disabled={!draft.consentRulesetId.trim()} onChange={(e) => set('consentAutoblocker', e.target.checked)} /> Auto-blocker: hold back tracking scripts until consent
          </label>
        </div>

        <h3 className="section-title">Head code</h3>
        <label className="form-row">
          HTML added to the &lt;head&gt; of every page: verification tags (Awin, Search Console), comments, scripts
          <textarea
            rows={5}
            spellCheck={false}
            className="code-input"
            value={draft.headHtml}
            placeholder={'<!-- Awin -->\n<meta name="google-site-verification" content="…">'}
            onChange={(e) => set('headHtml', e.target.value)}
          />
          <span className="muted small">Goes out exactly as written. Scripts that set cookies or track visitors must wait for consent.</span>
        </label>

        <h3 className="section-title">Copy and editorial</h3>
        <div className="form-grid cols-4">
          <label>
            Public site name
            <input value={draft.siteName} placeholder={site.brand.name} onChange={(e) => set('siteName', e.target.value)} />
          </label>
          <label>
            Descriptor
            <input value={draft.descriptor} placeholder="Voucher codes & deals" onChange={(e) => set('descriptor', e.target.value)} />
          </label>
          <label className="span-2">
            Tagline
            <input value={draft.tagline} onChange={(e) => set('tagline', e.target.value)} />
          </label>
          <label>
            Editorial owner
            <input value={draft.editorialOwner} onChange={(e) => set('editorialOwner', e.target.value)} />
          </label>
          <label>
            Search Console property
            <input value={draft.searchConsoleProperty} placeholder="sc-domain:example.com" onChange={(e) => set('searchConsoleProperty', e.target.value)} />
          </label>
          <label>
            Live coupons before indexing a merchant
            <input type="number" min={0} max={50} className="input-narrow" value={draft.thinMerchantMinLiveCoupons} onChange={(e) => set('thinMerchantMinLiveCoupons', Number(e.target.value))} />
          </label>
          <div className="mascot-field">
            <label>
              Mascot
              <input value={draft.mascotName} placeholder="none" onChange={(e) => set('mascotName', e.target.value)} />
            </label>
            <label className="inline-check">
              <input type="checkbox" checked={draft.mascotEnabled} disabled={!draft.mascotName.trim()} onChange={(e) => set('mascotEnabled', e.target.checked)} /> in microcopy
            </label>
          </div>
          <label className="span-4">
            Affiliate disclosure (shown next to coupons)
            <textarea rows={2} value={draft.affiliateDisclosure} placeholder="Platform default wording" onChange={(e) => set('affiliateDisclosure', e.target.value)} />
          </label>
          <label className="inline-check span-2">
            <input type="checkbox" checked={draft.commercialDisclosure} onChange={(e) => set('commercialDisclosure', e.target.checked)} /> Commercial disclosure on every page
          </label>
          <label className="inline-check span-2">
            <input type="checkbox" checked={draft.publisherCategories} onChange={(e) => set('publisherCategories', e.target.checked)} /> Use the publisher's own categories
          </label>
        </div>
        {site.settings.editorialTeam.length ? (
          <p className="muted small">Named editors: {site.settings.editorialTeam.map((e) => `${e.name} (${e.role})`).join(', ')}</p>
        ) : null}

        <h3 className="section-title">AI on this site</h3>
        {site.organization.noAiContent ? (
          <p className="dev-note">{site.organization.name} has "no AI content": AI writes nothing here, whatever is set below. Checks still run.</p>
        ) : null}
        <div className="ai-grid">
          <label className="inline-field">
            All capabilities
            <select value={draft.aiDefault} onChange={(e) => set('aiDefault', e.target.value as AiMode | '')}>
              <option value="">Platform default (suggest)</option>
              {options.aiModes.map((m) => (
                <option key={m} value={m}>
                  {AI_MODE_LABELS[m]}
                </option>
              ))}
            </select>
          </label>
          <table className="table compact">
            <thead>
              <tr>
                <th scope="col">Capability</th>
                <th scope="col">This site</th>
                <th scope="col">Organization allows</th>
                <th scope="col">In effect</th>
              </tr>
            </thead>
            <tbody>
              {options.aiCapabilities.map((capability) => (
                <tr key={capability} className={above(capability) ? 'row-warn' : undefined}>
                  <th scope="row">{CAPABILITY_LABELS[capability] ?? capability}</th>
                  <td>
                    <select
                      aria-label={`${CAPABILITY_LABELS[capability] ?? capability} on this site`}
                      value={draft.ai[capability] ?? ''}
                      onChange={(e) => setDraft((d) => ({ ...d, ai: { ...d.ai, [capability]: e.target.value as AiMode | '' } }))}
                    >
                      <option value="">Same as all</option>
                      {options.aiModes.map((m) => (
                        <option key={m} value={m} disabled={RANK[m] > RANK[ceilingFor(capability)]}>
                          {AI_MODE_LABELS[m]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>{AI_MODE_LABELS[ceilingFor(capability)]}</td>
                  <td className={above(capability) ? 'text-warn' : undefined}>{above(capability) ? `Above the ceiling` : AI_MODE_LABELS[effectiveFor(capability)]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </fieldset>
      {site.canAdmin ? (
        <div className="form-actions sticky-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || anyAbove}>
            Save settings
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setDraft(draftFrom(site))}>
            Reset
          </button>
          {anyAbove ? <span className="text-warn">Lower the highlighted capabilities to the organization ceiling.</span> : null}
        </div>
      ) : null}
    </form>
  );
}

function Addresses({ site }: { site: SiteDetail }) {
  const toast = useToast();
  const [hostname, setHostname] = useState('');
  const [basePath, setBasePath] = useState(site.type === 'publisher_subfolder' ? site.basePath : '/');
  if (!site.routed) {
    return <p className="empty">{TYPE_LABELS[site.type]} sites have no pages of their own, so no addresses. The host name {site.hostname} is used for origin checks.</p>;
  }
  const add = async (event: FormEvent) => {
    event.preventDefault();
    const done = await runWrite(toast, 'admin.domains.add', { siteRef: site.ref, hostname, basePath }, 'Address added');
    if (done) setHostname('');
  };
  return (
    <div>
      <p className="muted small">Requests for these host names and paths are served by this site. The primary address is the canonical one in links and sitemaps. Changes reach the sites within a minute (route cache).</p>
      <table className="table compact" aria-label="Addresses">
        <thead>
          <tr>
            <th scope="col">Host name</th>
            <th scope="col">Base path</th>
            <th scope="col">Primary</th>
            <th scope="col" className="actions">
              <span className="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {site.domains.map((domain) => (
            <tr key={domain.id}>
              <td>
                <code>{domain.hostname}</code>
              </td>
              <td>
                <code>{domain.basePath}</code>
              </td>
              <td>
                {domain.isPrimary ? (
                  <span className="badge badge-ok">Primary</span>
                ) : site.canAdmin ? (
                  <button type="button" className="btn btn-small" onClick={() => runWrite(toast, 'admin.domains.update', { id: domain.id, makePrimary: true }, `${domain.hostname} is now primary`)}>
                    Make primary
                  </button>
                ) : null}
              </td>
              <td className="actions">
                {site.canAdmin && !domain.isPrimary ? (
                  <ConfirmButton
                    label="Remove"
                    title="Remove this address?"
                    confirmLabel="Remove address"
                    onConfirm={() => runWrite(toast, 'admin.domains.remove', { id: domain.id }, 'Address removed')}
                  >
                    <p>
                      Requests for <code>{domain.hostname}{domain.basePath === '/' ? '' : domain.basePath}</code> will no longer reach {site.name}.
                    </p>
                  </ConfirmButton>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {site.canAdmin ? (
        <form className="inline-form" onSubmit={add} aria-label="Add address">
          <label>
            Host name
            <input required value={hostname} placeholder="www.example.com" onChange={(e) => setHostname(e.target.value)} />
          </label>
          <label>
            Base path
            <input value={basePath} placeholder="/" onChange={(e) => setBasePath(e.target.value)} />
          </label>
          <button type="submit" className="btn btn-primary">
            Add address
          </button>
        </form>
      ) : null}
    </div>
  );
}
